'use client';

import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import {
  type AppRole,
  type AppAction,
  canAccessRoute,
  canPerformAction,
  ROLE_META,
} from '@/lib/permissions';

// ─── Context ──────────────────────────────────────────────────────────────────

interface RoleContextValue {
  role: AppRole;
  setRole: (role: AppRole) => void;
  can: (action: AppAction) => boolean;
  canRoute: (pathname: string) => boolean;
  roleMeta: { color: string; initials: string };
}

const RoleContext = createContext<RoleContextValue | null>(null);

// ─── Provider ─────────────────────────────────────────────────────────────────

const DEFAULT_ROLE: AppRole = 'Procurement Officer';

export function RoleProvider({ children }: { children: ReactNode }) {
  const [role, setRoleState] = useState<AppRole>(DEFAULT_ROLE);

  const setRole = useCallback((r: AppRole) => setRoleState(r), []);

  const can = useCallback(
    (action: AppAction) => canPerformAction(role, action),
    [role]
  );

  const canRoute = useCallback(
    (pathname: string) => canAccessRoute(role, pathname),
    [role]
  );

  return (
    <RoleContext.Provider
      value={{ role, setRole, can, canRoute, roleMeta: ROLE_META[role] }}
    >
      {children}
    </RoleContext.Provider>
  );
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export function useRole(): RoleContextValue {
  const ctx = useContext(RoleContext);
  if (!ctx) throw new Error('useRole must be used within RoleProvider');
  return ctx;
}
